import type { GetEventTypeAvailabilitiesParams } from '@cocoonspace/sdk-js/v2/types/getEventTypeAvailabilitiesParams'
import { useEffect } from 'react'
import {
	getGetEventTypeAvailabilitiesQueryOptions,
	useGetEventTypeAvailabilities,
} from '@cocoonspace/sdk-js/v2/domains/schedulers/schedulers'
import { useQueryClient } from '@tanstack/react-query'

interface UseCachedAvailabilitiesOptions {
	staleTime?: number
	gcTime?: number
}

const DEFAULT_STALE_TIME = 1000 * 60 * 5 // 5 minutes
const DEFAULT_GC_TIME = 1000 * 60 * 10 // 10 minutes

/**
 * Hook to fetch event type availabilities with caching and prefetching.
 * Automatically prefetches the next month's data when the current month changes.
 */
export const useCachedEventTypeAvailabilities = (
	eventTypeId: string,
	params: GetEventTypeAvailabilitiesParams,
	options: UseCachedAvailabilitiesOptions = {},
) => {
	const queryClient = useQueryClient()
	const { staleTime = DEFAULT_STALE_TIME, gcTime = DEFAULT_GC_TIME } = options

	const month = Number(params.month)
	const year = Number(params.year)

	useEffect(
		function prefetchNextMonth() {
			const nextMonth = month === 12 ? 1 : month + 1
			const nextYear = month === 12 ? year + 1 : year

			void queryClient.prefetchQuery(
				getGetEventTypeAvailabilitiesQueryOptions(eventTypeId, {
					month: nextMonth.toString(),
					year: nextYear.toString(),
				}),
			)
		},
		[queryClient, eventTypeId, month, year],
	)

	return useGetEventTypeAvailabilities(eventTypeId, params, {
		query: {
			staleTime,
			gcTime,
		},
	})
}
